import { Component, OnInit, HostListener } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { faArrowLeft, faPlus, faSpinner, faBolt, faCog, faChevronUp, faChevronDown, faCalendar, faClock, faTimes, faAlignLeft, faList } from '@fortawesome/free-solid-svg-icons';

import { RetrieversService, Retriever, RetrieverExecution } from '../../services/retrievers.service';
import { ConfigurationsService, Configuration } from '../../services/configurations.service';
import { KnowledgeBaseService, KnowledgeBaseOption } from '../../services/knowledge-base.service';
import { AlertService } from '../../components/alert-service/alert-service';
import { TenantService } from '../../services/tenant.service';
import { TabsComponent, TabItem } from '../../components/tabs/tabs.component';
import { ModalSelectArchiveKbComponent, SelectedArchive } from '../../components/modal-select-archive-kb/modal-select-archive-kb.component';
import { ModalDeployKnowledgeBaseComponent, DeployableArchive } from '../../components/modal-deploy-knowledge-base/modal-deploy-knowledge-base.component';
import { ModalConfirmComponent } from '../../components/modal-confirm/modal-confirm.component';
import { ModelsFormComponent } from '../../components/models-form/models-form.component';
import { CanComponentDeactivate } from '../../interfaces/can-component-deactivate.interface';
import { SimulationExecutionsListComponent } from '../../components/simulation-executions-list/simulation-executions-list.component';
import { userListToDescriptions, descriptionsToUserList } from '../../services/retrievers.service';
import { SimulationFormShellComponent } from '../../components/simulation-form-shell/simulation-form-shell.component';
import { SimulationModelActionsComponent } from '../../components/simulation-model-actions/simulation-model-actions.component';

@Component({
  selector: 'app-retrievers-form',
  standalone: true,
  imports: [CommonModule, FormsModule, FontAwesomeModule, TranslateModule, NgSelectModule, TabsComponent, ModalSelectArchiveKbComponent, ModalDeployKnowledgeBaseComponent, ModalConfirmComponent, ModelsFormComponent, SimulationExecutionsListComponent, SimulationFormShellComponent, SimulationModelActionsComponent],
  templateUrl: './retrievers-form.component.html',
  styleUrls: ['./retrievers-form.component.scss'],
})
export class RetrieversFormComponent implements OnInit, CanComponentDeactivate {
  icons = { faArrowLeft, faPlus, faSpinner, faBolt, faCog, faChevronUp, faChevronDown, faCalendar, faClock, faTimes };
  configExpanded = false;

  isEditing = false;
  saving = false;
  loading = false;

  extractionTabs: TabItem[] = [
    { id: 'form', label: 'RETRIEVERS.TABS.FORM' },
    { id: 'result', label: 'INTERACTIONS.TABS.RESULTS' },
  ];
  activeTab = 'form';
  
  instructionTabs: TabItem[] = [
    { id: 'instructions', label: 'RETRIEVERS.TABS.INSTRUCTIONS', icon: faAlignLeft },
  ];
  fieldTabs: TabItem[] = [
    { id: 'fields', label: 'RETRIEVERS.TABS.FIELDS', icon: faList },
  ];

  executing = false;
  executions: RetrieverExecution[] = [];
  selectedExecutionId: string | null = null;
  currentResult: any = null;
  currentTokens: any = null;

  isAdmin = sessionStorage.getItem('role') === 'ADMIN';
  showSelectArchiveKb = false;
  showDeployKb = false;
  selectedArchiveForKb: DeployableArchive | null = null;

  private pristineSnapshot = '';

  userListInitial: Record<string, any> = {};
  showLeaveModal = false;
  private leaveSubject: Subject<boolean> | null = null;

  searchColumnDraft = '';

  form: Omit<Retriever, 'id' | 'created_at'> = {
    name: '',
    content: '',
    instructions: '',
    extraction_default_value: '',
    overwrite_cache: true,
    user_list: [],
    llm_model: '',
    knowledge: '',
    retriever: '',
    top_k: null,
    search_columns: [],
    delfos_server_config_id: null,
  };

  llmModelOptions: Configuration[] = [];
  retrieverOptions: Configuration[] = [];
  knowledgeOptions: KnowledgeBaseOption[] = [];
  delfosServerConfigs: Configuration[] = [];

  constructor(
    public route: ActivatedRoute,
    private router: Router,
    private service: RetrieversService,
    private configurationsService: ConfigurationsService,
    private knowledgeBaseService: KnowledgeBaseService,
    private alert: AlertService,
    private translate: TranslateService,
    private tenantService: TenantService,
  ) {}

  ngOnInit(): void {
    this.loadOptions();
    this.activeTab = this.route.snapshot.queryParamMap.get('tab') === 'result' ? 'result' : 'form';

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.loading = true;
      this.service.getById(id).subscribe({
        next: r => {
          if (r) {
            this.form = {
              name: r.name,
              content: r.content,
              instructions: r.instructions,
              extraction_default_value: r.extraction_default_value,
              overwrite_cache: r.overwrite_cache,
              user_list: JSON.parse(JSON.stringify(r.user_list)),
              llm_model: r.llm_model,
              knowledge: r.knowledge,
              retriever: r.retriever,
              top_k: r.top_k,
              search_columns: Array.isArray(r.search_columns) ? [...r.search_columns] : [],
              delfos_server_config_id: r.delfos_server_config_id ?? null,
            };
          }
          this.form.user_list = descriptionsToUserList(userListToDescriptions(this.form.user_list));
          this.userListInitial = userListToDescriptions(this.form.user_list);
          this.pristineSnapshot = JSON.stringify(this.form);
          this.loading = false;
        },
        error: () => {
          this.alert.showError(this.translate.instant('RETRIEVERS.ERRORS.LOAD_FAILED'));
          this.loading = false;
        },
      });
      this.loadExecutions(id);
    } else {
      this.form.top_k = 20;
      this.form.llm_model = 'M1.2';
      this.form.retriever = 'RE1.1';

      const state = history.state as { retrieverName?: string };
      if (state?.retrieverName) {
        this.form.name = state.retrieverName;
      }

      this.userListInitial = userListToDescriptions(this.form.user_list);
      this.pristineSnapshot = JSON.stringify(this.form);
    }
  }

  private loadExecutions(id: string): void {
    this.service.getExecutions(id).subscribe({
      next: execs => {
        this.executions = execs || [];
        if (this.executions.length > 0) {
          this.onSelectExecution(this.executions[0].id);
        }
      },
      error: () => { this.executions = []; },
    });
  }

  private loadOptions(): void {
    this.configurationsService.listAllowedForMe().subscribe({
      next: res => {
        const configurations = res.configurations || [];
        this.retrieverOptions = configurations.filter(c => c.type === 'retriever');
        this.llmModelOptions = configurations.filter(c => c.type === 'delfos_llm');
        this.delfosServerConfigs = configurations.filter(c => c.type === 'delfos_server');
      },
      error: () => {
        this.retrieverOptions = [];
        this.llmModelOptions = [];
        this.delfosServerConfigs = [];
      },
    });
    this.knowledgeBaseService.list().subscribe({
      next: bases => { this.knowledgeOptions = bases || []; },
      error: () => { this.knowledgeOptions = []; },
    });
  }

  openSelectArchiveKb(): void {
    this.showSelectArchiveKb = true;
  }

  onArchiveSelectedForKb(archive: SelectedArchive): void {
    this.showSelectArchiveKb = false;
    this.selectedArchiveForKb = archive;
    this.showDeployKb = true;
  }

  onKbDeployed(name: string): void {
    this.showDeployKb = false;
    this.selectedArchiveForKb = null;
    this.knowledgeBaseService.list().subscribe({
      next: bases => {
        this.knowledgeOptions = bases || [];
        if (this.knowledgeOptions.some(b => b.name === name)) {
          this.form.knowledge = name;
        }
      },
      error: () => {},
    });
  }

  private readonly SEARCH_COLUMNS_RETRIEVERS = new Set(['RE1.3']);
  private readonly MINIMAL_RETRIEVERS = new Set(['RE1.4']);

  get isSearchColumnsRetriever(): boolean {
    return this.SEARCH_COLUMNS_RETRIEVERS.has(this.form.retriever);
  }

  get isMinimalRetriever(): boolean {
    return this.MINIMAL_RETRIEVERS.has(this.form.retriever);
  }

  get showLlmModel(): boolean {
    return !this.isSearchColumnsRetriever && !this.isMinimalRetriever;
  }

  get showTopK(): boolean {
    return this.showLlmModel;
  }

  get showInstructions(): boolean {
    return this.showLlmModel;
  }

  get showUserList(): boolean {
    return !this.isMinimalRetriever;
  }

  get showSearchColumns(): boolean {
    return this.isSearchColumnsRetriever;
  }

  addSearchColumn(raw: string): void {
    const value = (raw || '').trim().replace(/,+$/, '').trim();
    if (!value) return;
    const list = this.form.search_columns || [];
    if (!list.includes(value)) {
      this.form.search_columns = [...list, value];
    }
    this.searchColumnDraft = '';
  }

  removeSearchColumn(index: number): void {
    const list = [...(this.form.search_columns || [])];
    list.splice(index, 1);
    this.form.search_columns = list;
  }

  get canSave(): boolean {
    const base = !!this.form.name?.trim() && !!this.form.content?.trim()
      && !!this.form.retriever && !!this.form.knowledge;
    return base && (!this.showLlmModel || !!this.form.llm_model);
  }

  get isDirty(): boolean {
    return this.pristineSnapshot !== '' && JSON.stringify(this.form) !== this.pristineSnapshot;
  }

  goBack(): void {
    this.router.navigate(this.tenantService.buildRoute('/retrievers'));
  }

  save(): void {
    if (!this.canSave) return;
    this.saving = true;
    const id = this.route.snapshot.paramMap.get('id');

    if (id) {
      this.service.update(id, this.form).subscribe({
        next: () => {
          this.alert.showSuccess(this.translate.instant('RETRIEVERS.SUCCESS.UPDATED'));
          this.saving = false;
          this.pristineSnapshot = JSON.stringify(this.form);
        },
        error: () => {
          this.alert.showError(this.translate.instant('RETRIEVERS.ERRORS.LOAD_FAILED'));
          this.saving = false;
        },
      });
    } else {
      this.service.create(this.form).subscribe({
        next: (newId) => {
          this.alert.showSuccess(this.translate.instant('RETRIEVERS.SUCCESS.CREATED'));
          this.saving = false;
          this.router.navigate(this.tenantService.buildRoute(`/retrievers/${newId}`));
        },
        error: () => {
          this.alert.showError(this.translate.instant('RETRIEVERS.ERRORS.LOAD_FAILED'));
          this.saving = false;
        },
      });
    }
  }

  execute(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id || this.executing) return;

    if (this.isDirty) {
      this.alert.showWarning(this.translate.instant('RETRIEVERS.EXECUTE.SAVE_FIRST'));
      return;
    }

    this.executing = true;
    this.activeTab = 'result';
    this.service.execute(id).subscribe({
      next: (execution) => {
        this.executions = [execution, ...this.executions];
        this.selectedExecutionId = execution.id;
        this.currentResult = execution.result;
        this.currentTokens = execution.tokens;
        this.executing = false;
        this.alert.showSuccess(this.translate.instant('RETRIEVERS.EXECUTE.SUCCESS'));
      },
      error: () => {
        this.executing = false;
        this.alert.showError(this.translate.instant('RETRIEVERS.EXECUTE.FAILED'));
      },
    });
  }

  onSelectExecution(executionId: string): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id || !executionId) return;
    this.selectedExecutionId = executionId;
    this.service.getExecution(id, executionId).subscribe({
      next: (execution) => {
        this.currentResult = execution.result;
        this.currentTokens = execution.tokens;
      },
      error: () => {},
    });
  }

  resultNumber(execution: RetrieverExecution): number {
    const idx = this.executions.findIndex(e => e.id === execution.id);
    return idx === -1 ? this.executions.length : this.executions.length - idx;
  }

  executionLabel(execution: RetrieverExecution): string {
    return this.translate.instant('RETRIEVERS.EXECUTE.RESULT_LABEL', { number: this.resultNumber(execution) });
  }

  toggleConfig(): void {
    this.configExpanded = !this.configExpanded;
  }

  get selectedExecution(): RetrieverExecution | null {
    return this.executions.find(e => e.id === this.selectedExecutionId) || null;
  }

  get delfosServerLabel(): string {
    if (!this.form.delfos_server_config_id) return '—';
    const cfg = this.delfosServerConfigs.find(c => c.id === this.form.delfos_server_config_id);
    return cfg ? (cfg.display_label || cfg.key) : '—';
  }

  get llmModelLabel(): string {
    const cfg = this.llmModelOptions.find(c => c.key === this.form.llm_model);
    return cfg ? (cfg.display_label || cfg.key) : (this.form.llm_model || '—');
  }

  get retrieverLabel(): string {
    const cfg = this.retrieverOptions.find(c => c.key === this.form.retriever);
    return cfg ? (cfg.display_label || cfg.key) : (this.form.retriever || '—');
  }

  get knowledgeLabel(): string {
    if (!this.form.knowledge) return '—';
    const kb = this.knowledgeOptions.find(k => k.name === this.form.knowledge);
    return kb?.container_name ? `${this.form.knowledge} (${kb.container_name})` : this.form.knowledge;
  }

  get selectedConfig(): {
    llm_model: string; retriever: string; knowledge: string;
    top_k: number | null; delfos_server: string; overwrite_cache: boolean;
  } {
    const cfg = this.selectedExecution?.config;
    if (cfg) {
      return {
        llm_model: cfg.llm_model || '—',
        retriever: cfg.retriever || '—',
        knowledge: cfg.knowledge || '—',
        top_k: cfg.top_k ?? null,
        delfos_server: cfg.delfos_server || '—',
        overwrite_cache: !!cfg.overwrite_cache,
      };
    }
    return {
      llm_model: this.llmModelLabel,
      retriever: this.retrieverLabel,
      knowledge: this.form.knowledge || '—',
      top_k: this.form.top_k,
      delfos_server: this.delfosServerLabel,
      overwrite_cache: this.form.overwrite_cache,
    };
  }

  onUserListChange(value: Record<string, any>): void {
    this.form.user_list = descriptionsToUserList(value);
  }

  canDeactivate(): boolean | Observable<boolean> {
    if (!this.isDirty) {
      return true;
    }
    this.leaveSubject = new Subject<boolean>();
    this.showLeaveModal = true;
    return this.leaveSubject.asObservable();
  }

  onLeaveConfirm(): void {
    this.showLeaveModal = false;
    this.leaveSubject?.next(true);
    this.leaveSubject?.complete();
    this.leaveSubject = null;
  }

  onLeaveCancel(): void {
    this.showLeaveModal = false;
    this.leaveSubject?.next(false);
    this.leaveSubject?.complete();
    this.leaveSubject = null;
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnloadHandler(event: BeforeUnloadEvent): void {
    if (this.isDirty) {
      event.preventDefault();
      event.returnValue = '';
    }
  }
}
